import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './AppShell';
import { PortalShell } from '../features/portal/PortalShell';
import { NotFoundPage } from '../shared/components/NotFoundPage';
import { CurrencyDisplayProvider } from '../shared/components/CurrencyDisplayProvider';
import { ErrorState, LoadingState, PoweredBy } from '../shared/components/ui';
import { AuthProvider, useAuth } from '../features/auth/AuthContext';
import { ensureClientConfiguration } from '../shared/api/http';
import { LoginPage } from '../features/auth/LoginPage';
import { OfflineUnlockPage } from '../features/auth/OfflineUnlockPage';
import { TenantSelectionPage } from '../features/auth/TenantSelectionPage';
import { OfflineProvider } from '../offline/OfflineProvider';
import { InstallBanner } from '../pwa/InstallBanner';
import { UpdateBanner } from '../pwa/UpdateBanner';
import { useStackedTables } from '../shared/lib/stackedTables';

const LandingPage = lazy(() => import('../features/landing/LandingPage'));
const SuperAdminPage = lazy(() => import('../features/administration/SuperAdminPage'));
const AiControlCenterPage = lazy(() => import('../features/administration/AiControlCenterPage'));
const IntegrationControlCenterPage = lazy(() => import('../features/administration/IntegrationControlCenterPage'));
const PlatformAuditLogPage = lazy(() => import('../features/audit/PlatformAuditLogPage'));
const InterOrganizationIdentityRegisterPage = lazy(() => import('../features/interOrganizationIdentity/InterOrganizationIdentityRegisterPage'));
const InterOrganizationIdentityConfirmPage = lazy(() => import('../features/interOrganizationIdentity/InterOrganizationIdentityConfirmPage'));
const InterOrganizationIdentityCodePage = lazy(() => import('../features/interOrganizationIdentity/InterOrganizationIdentityCodePage'));
const ForgotPasswordPage = lazy(() => import('../features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../features/auth/ResetPasswordPage'));
const RegistrationInvitationsPage = lazy(() => import('../features/invitations/RegistrationInvitationsPage'));
const WorkforceSelfServicePage = lazy(() => import('../features/workforce/WorkforceSelfServicePage'));
const PortalOverviewPage = lazy(() => import('../features/portal/PortalOverviewPage'));
const PortalDocumentsPage = lazy(() => import('../features/portal/PortalDocumentsPage'));
const PortalIdentityPage = lazy(() => import('../features/portal/PortalIdentityPage'));

function AuthenticatedRoutes() {
  const { phase, selectedTenant, retryHydration, session, tenants } = useAuth();
  const location = useLocation();
  // An external provider bounces back to the root with a query only the login form can finish.
  const query = new URLSearchParams(location.search);
  const resumingSignIn = query.has('mfa') || query.has('authError');

  // The root is always the public site — signed in or not, before the session
  // probe resolves, and even when the API is unreachable. The workspace lives
  // under `/console`, so returning to the homepage is always one link away.
  if (location.pathname === '/' && !resumingSignIn) {
    return <Suspense fallback={<LoadingState />}><LandingPage /></Suspense>;
  }

  // Registering a Varza Inter-organization Identity, confirming the email that issues it, and reading a scanned card
  // all belong to someone who has no session yet, so they resolve before the session gate.
  if (location.pathname.startsWith('/vii')) {
    return (
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route path="/vii/register" element={<InterOrganizationIdentityRegisterPage />} />
          <Route path="/vii/confirm" element={<InterOrganizationIdentityConfirmPage />} />
          <Route path="/vii/:code" element={<InterOrganizationIdentityCodePage />} />
          <Route path="/vii" element={<Navigate to="/vii/register" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Account creation and recovery must remain reachable without a session. `/register` and `/verify`
  // are compatibility aliases for links issued by the public website and older notification templates.
  if (['/register', '/verify', '/forgot-password', '/reset-password'].includes(location.pathname)) {
    return (
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route path="/register" element={<InterOrganizationIdentityRegisterPage />} />
          <Route path="/verify" element={<InterOrganizationIdentityConfirmPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (phase === 'loading') return <LoadingState />;
  // No connection, but this device holds a copy the operator's password will open.
  if (phase === 'offline-locked') return <OfflineUnlockPage />;
  if (phase === 'error') {
    return (
      <main className="auth-page">
        <div className="auth-card"><ErrorState retry={() => void retryHydration()} /></div>
        <PoweredBy />
      </main>
    );
  }
  if (phase === 'anonymous') return <LoginPage />;
  if (location.pathname === '/invitations') {
    if (session?.isSuperAdmin !== true && session?.canCreateInvitations !== true) return <NotFoundPage />;
    return <Suspense fallback={<LoadingState />}><RegistrationInvitationsPage /></Suspense>;
  }
  const isLegacyAdminPath = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  if (session?.isSuperAdmin && isLegacyAdminPath) {
    const platformPath = `/platform${location.pathname.slice('/admin'.length)}${location.search}`;
    return <Navigate to={platformPath} replace />;
  }
  const isPlatformPath = location.pathname === '/platform' || location.pathname.startsWith('/platform/');
  if (session?.isSuperAdmin && isPlatformPath) {
    return (
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route path="/platform" element={<SuperAdminPage />} />
          <Route path="/platform/ai" element={<AiControlCenterPage />} />
          <Route path="/platform/integrations" element={<IntegrationControlCenterPage />} />
          <Route path="/platform/audit" element={<PlatformAuditLogPage />} />
          <Route path="/platform/*" element={<SuperAdminPage />} />
        </Routes>
      </Suspense>
    );
  }
  // The light portal: a Varza Inter-organization Identity holder reaches it whether or not they belong to a business,
  // and someone who belongs to none is sent there rather than to a tenant list with nothing in it.
  const isPortalPath = location.pathname === '/portal' || location.pathname.startsWith('/portal/');
  if (isPortalPath) {
    if (session?.interOrganizationIdentityCode == null) return <Navigate to="/console" replace />;
    return (
      <Suspense fallback={<LoadingState />}>
        <Routes>
          <Route path="/portal" element={<PortalShell />}>
            <Route index element={<PortalOverviewPage />} />
            <Route path="documents" element={<PortalDocumentsPage />} />
            <Route path="workforce" element={<WorkforceSelfServicePage />} />
            <Route path="identity" element={<PortalIdentityPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }
  if (selectedTenant === null) {
    if (tenants.length === 0 && session?.interOrganizationIdentityCode != null) return <Navigate to="/portal" replace />;
    return <TenantSelectionPage />;
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        {/* The sign-in form has no meaning once the session exists. */}
        <Route path="login" element={<Navigate to="/console" replace />} />
        {/*
          The console is one route rather than twenty. Everything under `/console` is drawn by
          the workspace, which holds several of those addresses open at once and lifts whichever
          document is in front back into the browser bar — so a link, a bookmark, and the back
          button all still mean what they meant before.
        */}
        <Route path="/console/*" element={<AppShell />} />
        {/* Anything outside the console and the site is still a dead address. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
function AuthenticatedCurrencyRoutes() {
  const { phase } = useAuth();
  return (
    <CurrencyDisplayProvider enabled={phase === 'authenticated'}>
      <AuthenticatedRoutes />
    </CurrencyDisplayProvider>
  );
}


export function App() {
  // Registers re-flow into labelled cards on small screens; the labels come from the
  // table headings, which only script can carry across to the body cells.
  useStackedTables();

  // Fetching the browser's security configuration here rather than on the first protected form means the
  // reCAPTCHA script is already warm by the time anyone submits one. It is site-wide because the protected
  // routes are: sign-in, registration, and every public write that costs a message.
  useEffect(() => {
    void ensureClientConfiguration();
  }, []);

  return (
    // The offline layer wraps the session rather than the other way round: it has to be in place
    // before the first session probe, because whether that probe can be answered from the device
    // is what decides between the sign-in form and the offline unlock screen.
    <OfflineProvider>
      <AuthProvider>
        <AuthenticatedCurrencyRoutes />
      </AuthProvider>
      <UpdateBanner />
      <InstallBanner />
    </OfflineProvider>
  );
}
